import { Button, Card, VidarioLogo } from "@vidario/ui";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { useEffect, type ReactElement } from "react";
import { isRouteErrorResponse, useNavigate, useRouteError } from "react-router";

import { captureException } from "./platform/observability";

interface ErrorInfo {
  title: string;
  message: string;
  detail?: string | undefined;
  status?: number | undefined;
}

function classify(err: unknown): ErrorInfo {
  if (isRouteErrorResponse(err)) {
    if (err.status === 404) {
      return {
        title: "Seite nicht gefunden",
        message:
          "Die aufgerufene Adresse existiert nicht. Möglicherweise wurde sie verschoben oder entfernt.",
        status: 404,
      };
    }
    if (err.status === 401 || err.status === 403) {
      return {
        title: "Kein Zugriff",
        message:
          "Für diese Ansicht fehlt dir die Berechtigung. Bitte bei deinem Admin melden.",
        status: err.status,
      };
    }
    return {
      title: `Fehler ${err.status}`,
      message: err.statusText || "Unerwartete Antwort vom Server.",
      status: err.status,
    };
  }
  if (err instanceof Error) {
    return {
      title: "Etwas ist schiefgelaufen",
      message: "Die Seite konnte nicht geladen werden. Bitte lade die Seite neu.",
      detail: err.message,
    };
  }
  return {
    title: "Etwas ist schiefgelaufen",
    message: "Die Seite konnte nicht geladen werden. Bitte lade die Seite neu.",
  };
}

const NOT_FOUND: ErrorInfo = {
  title: "Seite nicht gefunden",
  message:
    "Die aufgerufene Adresse existiert nicht. Möglicherweise wurde sie verschoben oder entfernt.",
  status: 404,
};

/** Global react-router error boundary. Shown whenever a route's
 * render throws, a loader rejects (we have none), or the router hits
 * an unknown URL (404). Keeps the TopNav-less look so the user can
 * recover even when the shell itself is what failed.
 *
 * When rendered as a plain catch-all Component (not as `errorElement`),
 * `useRouteError()` returns undefined; we then show the NOT_FOUND
 * variant explicitly. */
export function RouteError(): ReactElement {
  const err = useRouteError();
  const navigate = useNavigate();
  // React Router v7 delivers `null` to the errorElement when no route
  // matches at all (the classic 404). Treat both null and undefined as
  // the "not found" case — anything truthy goes through classify().
  const info = err == null ? NOT_FOUND : classify(err);

  useEffect(() => {
    if (err == null) return; // 404 catch-all — expected, not an error.
    console.error("[RouteError]", err);
    // Expected auth / not-found responses aren't worth tracking; only
    // thrown errors and 5xx route responses go to Sentry/GlitchTip.
    // No-op unless the SDK was initialised (see platform/observability).
    if (isRouteErrorResponse(err) && err.status < 500) return;
    captureException(err);
  }, [err]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-neutral-50 px-6 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <VidarioLogo />
        </div>
        <Card padded className="text-center">
          {info.status != null && (
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[2px] text-text-tertiary">
              Fehler {info.status}
            </div>
          )}
          <h1 className="m-0 mb-2 text-xl font-semibold">{info.title}</h1>
          <p className="m-0 text-sm leading-relaxed text-text-secondary">{info.message}</p>
          {info.detail && import.meta.env.DEV && (
            <pre className="mt-4 max-h-40 overflow-auto rounded-sm bg-neutral-100 p-3 text-left font-mono text-[11px] text-text-secondary">
              {info.detail}
            </pre>
          )}
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<ArrowLeft size={14} />}
              onClick={() => void navigate("/dashboard")}
            >
              Zum Dashboard
            </Button>
            <Button
              size="sm"
              leftIcon={<RefreshCw size={14} />}
              onClick={() => window.location.reload()}
            >
              Seite neu laden
            </Button>
          </div>
        </Card>
      </div>
    </main>
  );
}
